import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { primaryCTAShort, primaryCTATarget } from "../../lib/config";
import { BRAND_LOGO } from "../../lib/assets";

const navLinks = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "The Game",     href: "#whats-in-the-box" },
  { label: "FAQ",          href: "#faq" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Force light-header appearance on non-home pages (which have cream backgrounds)
  const isLightPage = location.pathname !== "/";
  const useLightHeader = scrolled || isLightPage;

  const handleNav = (href) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePrimary = () => {
    setMobileOpen(false);
    const target = primaryCTATarget();
    if (target.startsWith("#")) {
      if (location.pathname !== "/") navigate("/" + target);
      else handleNav(target);
    } else {
      navigate(target);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,box-shadow] duration-300 ${
        useLightHeader
          ? "bg-white/90 backdrop-blur-xl shadow-[0_1px_0_0_rgba(14,26,43,0.08)]"
          : "bg-[color:var(--pw-canvas)]/40 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-24 md:h-28 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-center gap-2 group"
          aria-label="People Watching — home"
        >
          <img
            src={BRAND_LOGO}
            alt="People Watching"
            className="h-20 md:h-24 w-auto rounded-full transition-transform duration-300 group-hover:scale-105"
            draggable="false"
          />
          <span className="sr-only">People Watching</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navLinks.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              onClick={() => handleNav(l.href)}
              className="text-sm font-medium hover:text-[color:var(--pw-yellow)] transition-colors"
              style={{ color: useLightHeader ? "var(--pw-navy)" : "rgba(255,255,255,0.9)" }}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={handlePrimary}
            data-testid="header-primary-cta"
            className="pw-btn-primary hidden md:inline-flex !py-2.5 !px-5"
          >
            {primaryCTAShort()}
          </button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-toggle"
                className="lg:hidden w-11 h-11 rounded-full flex items-center justify-center border"
                style={{
                  borderColor: useLightHeader ? "rgba(14,26,43,0.15)" : "rgba(255,255,255,0.25)",
                  color: useLightHeader ? "var(--pw-navy)" : "#fff",
                }}
                aria-label="Open menu"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85%] max-w-sm p-0 border-none">
              <div className="h-full navy-bg text-white p-8 flex flex-col">
                <div className="mb-10 mt-2 flex items-center gap-2">
                  <img
                    src={BRAND_LOGO}
                    alt="People Watching"
                    className="h-20 w-auto rounded-full"
                    draggable="false"
                  />
                </div>
                <div className="flex flex-col gap-4">
                  {navLinks.map((l) => (
                    <button
                      key={l.href}
                      data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                      onClick={() => handleNav(l.href)}
                      className="text-left font-display text-3xl font-bold tracking-tight hover:text-[color:var(--pw-yellow)] transition-colors"
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
                <div className="mt-auto">
                  <button
                    onClick={handlePrimary}
                    className="pw-btn-primary w-full"
                    data-testid="mobile-primary-cta"
                  >
                    {primaryCTAShort()}
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
